import React from "react";
import Icon from "../../../system/components/icon";
import Avatar from "../../../system/components/avatar";
import PrimeDatePicker from "../../../system/components/prime-date-picker";
import {DECISION_STATUS, MEETING_STATUS, TASK_STATUS, dayOfSeconds, daySeconds, faNum, linkOf} from "./meetings-labels";

// Small shared pieces of the meetings tab (docs/tasks/26-project-meetings.md).

const KINDS = {meeting: MEETING_STATUS, decision: DECISION_STATUS, task: TASK_STATUS};

export function StatusPill({kind = "decision", status}) {
    const entry = KINDS[kind]?.[status];
    if (!entry) return null;
    return <span className={`pd-pill tone-${entry.tone}`}>{entry.label}</span>;
}

export const personOf = (tab, key) => tab.people.find((item) => item.key === key) || null;

export function PersonInline({tab, personKey, prefix = "", empty = "بدون مسئول"}) {
    const person = personOf(tab, personKey);
    return (
        <span className="mt-person">
            {person?.member && <Avatar memberData={person.member} size="normal"/>}
            <span>{`${prefix}${person?.name || empty}`}</span>
        </span>
    );
}

export function AvatarStack({tab, keys, max = 4}) {
    const shown = keys.slice(0, max);
    return (
        <span className="mt-avatar-stack" aria-label={keys.map((key) => personOf(tab, key)?.name).filter(Boolean).join("، ")}>
            {shown.map((key) => {
                const person = personOf(tab, key);
                return person?.member ? <Avatar key={key} memberData={person.member} size="normal" tooltip/> : null;
            })}
            {keys.length > max && <span className="mt-avatar-more">{`+${faNum(keys.length - max)}`}</span>}
        </span>
    );
}

export function Progress({value, tone = "green"}) {
    const width = Math.max(0, Math.min(100, Number(value) || 0));
    return (
        <span className="mt-progress" role="img" aria-label={`${faNum(width)} درصد`}>
            <span className="pd-bar flex-1"><span className={`tone-${tone}`} style={{width: `${width}%`}}/></span>
            <b>{`${faNum(width)}٪`}</b>
        </span>
    );
}

// meeting → decision → tasks → result → goal / milestone
export function Chain({items}) {
    return (
        <div className="mt-chain" aria-label="زنجیره‌ی پیگیری">
            {items.map((item, index) => (
                <React.Fragment key={item.label}>
                    {index > 0 && <Icon icon="mi mi:chevron_left" className="mt-chain-arrow"/>}
                    <span className={`mt-chain-step ${item.done ? "is-done" : ""} ${item.active ? "is-active" : ""}`}>{item.label}</span>
                </React.Fragment>
            ))}
        </div>
    );
}

export function SectionHead({icon, title, sub, action}) {
    return (
        <div className="mt-section-head">
            <span className="mt-section-icon"><Icon icon={`mi mi:${icon}`}/></span>
            <div className="flex-1 min-w-0">
                <h3 className="pd-title text-sm">{title}</h3>
                {sub && <p className="pd-sub">{sub}</p>}
            </div>
            {action}
        </div>
    );
}

export function Empty({icon = "inbox", text}) {
    return (
        <div className="mt-empty">
            <Icon icon={`mi mi:${icon}`}/>
            <span>{text}</span>
        </div>
    );
}

// a Persian date picker for a "YYYY-MM-DD" day (the shared picker speaks Unix seconds)
export function DayPicker({value, onChange, disabled = false, placeholder = "تاریخ"}) {
    return (
        <span className={disabled ? "pointer-events-none opacity-60" : ""}>
            <PrimeDatePicker type="PERSIAN_DATE" inputClassName="w-full text-xs" placeholder={placeholder} value={daySeconds(value)} onChange={(event) => onChange(dayOfSeconds(event.value))}/>
        </span>
    );
}

// the meeting place; a link in it opens in a new tab
export function LocationText({text, className = ""}) {
    if (!text) return null;
    const url = linkOf(text);
    return url
        ? <a href={url} target="_blank" rel="noopener noreferrer" className={`mt-link ${className}`} onClick={(event) => event.stopPropagation()}><Icon icon="mi mi:videocam"/>{text}</a>
        : <span className={className}><Icon icon="mi mi:location_on"/>{text}</span>;
}
